import {ChangeEvent, FC, useContext, useMemo, useState} from 'react';
import Button from '@/shared/components/Button';
import Switch from '@/shared/components/Switches/Switch';
import {OUTLINE} from '@/utils/constants';
import {Box, Icon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
import {ReactComponent as WarningIcon} from '@/assets/icons/sidebar/warning-2.svg';
import {ReactComponent as SuccessIcon} from '@/assets/icons/successfully.svg';
import {ReactComponent as ErrorIcon} from '@/assets/icons/close-square.svg';
import {UploadContactListContext} from '../../../UploadContactListContext';
import {IOrganizationContactResponse} from '@/utils/types';
import {OrganizationContactService} from '@/utils/services';
import Checkbox from '@/shared/components/Checkbox';
import Modal from '@/shared/components/Modal';
import {validateDataTableStyle} from './ValidateDataTable.style';
import {useGetOrganizationId} from '@/utils/hooks';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';

interface IValidateDataTableProps {
	onClose: () => void;
}

const ValidateDataTable: FC<IValidateDataTableProps> = ({onClose}) => {
	const organizationId = useGetOrganizationId();

	const {existingContactList, setExistingContactList, manageDuplicate, columnForDuplicate} =
		useContext(UploadContactListContext);

	const [contactsWithError, setContactsWithError] = useState<IOrganizationContactResponse[]>([]);
	const [contactsToDelete, setContactsToDelete] = useState<IOrganizationContactResponse[]>([]);
	const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const [checked, setChecked] = useState(false);

	const [contactsData, setContactsData] = useState<IOrganizationContactResponse[]>(
		(existingContactList as IOrganizationContactResponse[]) || []
	);

	const dataWithError = useMemo(() => {
		if (!columnForDuplicate) return [];

		const groupedMap = new Map<string, IOrganizationContactResponse[]>();

		existingContactList.forEach((item: IOrganizationContactResponse) => {
			const key = String(item[columnForDuplicate as keyof IOrganizationContactResponse]);
			if (!groupedMap.has(key)) {
				groupedMap.set(key, []);
			}
			groupedMap.get(key)!.push(item);
		});

		const result: IOrganizationContactResponse[] = [];

		groupedMap.forEach((group) => {
			if (group.length > 1) {
				if (manageDuplicate === 'UpdateExisting') {
					result.push(...group.slice(0, -1));
				} else if (manageDuplicate === 'Skip') {
					result.push(...group.slice(1));
				}
			}
		});
		setContactsWithError(result);
		return result;
	}, [manageDuplicate, columnForDuplicate]);

	const handleWarningModal = () => setIsWarningModalOpen(!isWarningModalOpen);

	const handleSuccessModal = () => {
		if (isSuccessModalOpen) {
			onClose();
		}
		setIsSuccessModalOpen(!isSuccessModalOpen);
	};

	const setContactData = async () => {
		if (!organizationId) return;

		const clearData = contactsData.filter((item) => !contactsWithError.includes(item));

		if (clearData.length === 0) {
			toast.error('No valid contacts to import. Please review the errors and try again.');
			return;
		}

		const reqBody = {
			contacts: clearData,
			organizationId,
			manageDuplicate,
			columnForDuplicate,
		};

		try {
			await OrganizationContactService.setContactList(reqBody);
			handleSuccessModal();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const handleConfirmClick = async () => {
		if (contactsWithError.length > 0) {
			handleWarningModal();
			return;
		}
		setContactData();
	};

	const handleDeleteContact = () => {
		const clearData = contactsData.filter((item) => !contactsToDelete.includes(item));
		setContactsToDelete([]);
		setContactsData(clearData);
		setExistingContactList(clearData);
	};

	const handleShowOnlyContactsWithErrors = (e: ChangeEvent<HTMLInputElement>) => {
		setChecked(!checked);

		if (contactsWithError.length === 0) {
			e.target.checked && setContactsData([]);
			!e.target.checked &&
				setContactsData(
					existingContactList.filter((item: IOrganizationContactResponse) => !dataWithError.includes(item))
				);
		}

		if (contactsWithError.length > 0) {
			e.target.checked && setContactsData(contactsWithError);
			!e.target.checked && setContactsData(existingContactList as IOrganizationContactResponse[]);
			setContactsToDelete([]);
		}
	};

	return (
		<Box>
			<Box sx={validateDataTableStyle.wrapper}>
				<Typography sx={validateDataTableStyle.title}>Validate data:</Typography>
				<Box sx={validateDataTableStyle.btnWrapper}>
					<Button
						title="Discard Selected Rows"
						variant={OUTLINE}
						onClick={handleDeleteContact}
						disabled={!contactsToDelete.length}
					/>
					<Box sx={validateDataTableStyle.switch}>
						<Switch
							checked={checked}
							onChange={(e: ChangeEvent<HTMLInputElement>) => handleShowOnlyContactsWithErrors(e)}
						/>
						<Typography sx={validateDataTableStyle.text}>Show only rows with errors</Typography>
					</Box>
				</Box>
			</Box>

			<TableContainer sx={validateDataTableStyle.tableContainer}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell sx={validateDataTableStyle.tableHead} />
							<TableCell sx={validateDataTableStyle.tableHead}>First name</TableCell>
							<TableCell sx={validateDataTableStyle.tableHead}>Last name</TableCell>
							<TableCell sx={validateDataTableStyle.tableHead}>Email</TableCell>
							<TableCell sx={validateDataTableStyle.tableHead}>Phone</TableCell>
							<TableCell sx={validateDataTableStyle.tableHead}>Meal</TableCell>
							<TableCell sx={validateDataTableStyle.tableHead}>Address</TableCell>
							<TableCell sx={validateDataTableStyle.tableHead}>City</TableCell>
							<TableCell sx={validateDataTableStyle.tableHead}>State</TableCell>
							<TableCell sx={validateDataTableStyle.tableHead}>Zip</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{contactsData.length > 0 &&
							contactsData.map((row: any, index) => (
								<TableRow key={row._internalId}>
									<TableCell style={{padding: '0', width: '42px', height: '42px'}}>
										{contactsWithError.includes(row) ? (
											<Icon sx={validateDataTableStyle.errorIcon}>
												<ErrorIcon />
											</Icon>
										) : (
											<Checkbox
												checked={contactsToDelete.includes(row)}
												onChange={() =>
													setContactsToDelete((prev) =>
														prev.includes(row) ? prev.filter((item) => item !== row) : [...prev, row]
													)
												}
											/>
										)}
									</TableCell>
									<TableCell sx={validateDataTableStyle.tableCell}>{row?.firstName}</TableCell>
									<TableCell sx={validateDataTableStyle.tableCell}>{row?.lastName}</TableCell>
									<TableCell sx={validateDataTableStyle.tableCell}>{row?.email}</TableCell>
									<TableCell sx={validateDataTableStyle.tableCell}>{row?.phoneNumber}</TableCell>
									<TableCell sx={validateDataTableStyle.tableCell}>{row?.meals.join(', ')}</TableCell>
									<TableCell sx={validateDataTableStyle.tableCell}>{row?.address}</TableCell>
									<TableCell sx={validateDataTableStyle.tableCell}>{row?.city}</TableCell>
									<TableCell sx={validateDataTableStyle.tableCell}>{row?.state}</TableCell>
									<TableCell sx={validateDataTableStyle.tableCell}>{row?.zip}</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
			<Box sx={validateDataTableStyle.buttonWrapper}>
				<Button
					title="Confirm"
					disabled={existingContactList.length === 0 || contactsData.length === 0 || contactsData === contactsToDelete}
					onClick={handleConfirmClick}
				/>
			</Box>

			<Modal
				open={isWarningModalOpen}
				onClose={handleWarningModal}
				title="Some Rows Contain Errors"
				description="Rows with errors will be ignored when submitting."
				mainIcon={
					<Icon sx={validateDataTableStyle.warningIcon}>
						<WarningIcon />
					</Icon>
				}
				color="warning"
				mainButtonTitle="Continue"
				closeButtonTitle="Cancel"
				onSave={setContactData}
				sx={validateDataTableStyle.modal}
				isShowCloseButton
				isShowSaveButton
			/>

			<Modal
				open={isSuccessModalOpen}
				onClose={handleSuccessModal}
				title="Contact List Imported"
				description="Your contact list has been successfully imported."
				mainIcon={
					<Icon sx={validateDataTableStyle.warningIcon}>
						<SuccessIcon />
					</Icon>
				}
				sx={validateDataTableStyle.modal}
				onSave={handleSuccessModal}
				mainButtonTitle="Done"
				isShowSaveButton
			/>
		</Box>
	);
};

export default ValidateDataTable;
